import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Box, Chip, IconButton, useMediaQuery } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DoneIcon from "@mui/icons-material/Done";
import ChatIcon from "@mui/icons-material/Chat";
import DocumentsIcon from "@mui/icons-material/InsertDriveFile";
import moment from "moment";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import { getOptionLabel, getOptionValue } from "common/enums";
import Modal from "Components/Common/Modal";
import AddNewUtilityIssue from "./AddNewUtilityIssue";
import ComplaintConversation from "./ComplaintConversationModal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Tooltip from "Components/Common/Tooltip";
import Backdrop from "Components/Common/Backdrop";
import ImageGalleryModal from "./ImageGalleryModal";
import FilterComplaintList from "./FilterComplaintList";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useLazyConsumerComplaintQuery,
  usePutConsumerComplaintMutation,
} from "apis/ConsumerComplaint";

import { CONSUMER_COMPLAINT } from "tagTypes";

import InprogressIcon from "assets/images/icons8-in-progress-32.png";

import { ComplaintDescription } from "./style";
import { useSelector } from "react-redux";
import { POWER_READ_ADMIN } from "common/constant";

const RECORDS_PER_PAGE = 10;

const chipColor = {
  1: "info",
  2: "success",
  3: "error",
};

const ViewAllComplaints = ({ t }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const resetPageRef = useRef();

  const { roles } = useSelector((state) => state.Login);
  const isReadAdmin = roles.includes(POWER_READ_ADMIN);

  const lg = useMediaQuery("(min-width: 900px) and (max-width: 1535px)");
  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const [
    complaintIdForOpenStatusDialogModal,
    setComplaintIdForOpenStatusDialogModal,
  ] = useState(null);
  const [
    complaintIdForCloseStatusDialogModal,
    setComplaintIdForCloseStatusDialogModal,
  ] = useState(null);

  const [getDocumentUrlsById, { isLoading, isFetching }] =
    useLazyConsumerComplaintQuery();
  const [isOpenNewUtilityModal, setIsOpenNewUtilityModal] = useState(false);
  const [query, setQuery] = useState({});

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [newUtilityData, setNewUtilityData] = useState({});
  const [documentUrls, setDocumentUrls] = useState([]);

  const [updateComplaintStatus] = usePutConsumerComplaintMutation();

  const complaintId = Number(searchParams.get("complaintId"));
  const complaintStatus = searchParams.get("status");

  const complaintIdForViewDocument = Number(
    searchParams.get("complaintIdForDocumentView")
  );

  const columns = [
    {
      title: "Customer Number",
      field: "consumerNumber",
      columnProps: {
        width: "15%",
      },
    },
    {
      title: "Utility Name",
      field: "utility.name",
      columnProps: {
        width: "10%",
      },
    },
    {
      title: "Support Case",
      field: "supportCaseId",
      columnProps: {
        width: "15%",
      },
    },
    {
      title: "Priority",
      renderCell: ({ severity }) => getOptionLabel("severityOptions", severity),
      columnProps: {
        width: "5%",
      },
    },
    {
      title: "Date",
      columnProps: {
        width: "10%",
      },
      renderCell: (item) =>
        moment(item.createdAt).isValid()
          ? convertUTCDateToLocalDate(item.createdAt).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Actions",
      columnProps: {
        width: "20%",
      },
      renderCell: ({
        complaintDocuments,
        complaintStatus,
        id,
        utility,
        utilityIssueName,
      }) => {
        const status = getOptionLabel(
          "complaintStatus",
          complaintStatus
        ).toLowerCase();
        return (
          <Box width="145px">
            <IconButton
              disabled={!complaintDocuments}
              onClick={() =>
                setSearchParams({ complaintIdForDocumentView: id })
              }
            >
              <Tooltip title={t("View Documents")}>
                <DocumentsIcon
                  fontSize="small"
                  color={!complaintDocuments ? "disabled" : "primary"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>

            <IconButton
              onClick={() => {
                setSearchParams({
                  complaintId: id,
                  status: getOptionLabel("complaintStatus", complaintStatus),
                });
              }}
              disabled={status === "created"}
            >
              <Tooltip title={t("View Conversation")}>
                <ChatIcon
                  fontSize="small"
                  color={status === "created" ? "disabled" : "primary"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>

            {status === "created" ? (
              <IconButton
                onClick={() => setComplaintIdForOpenStatusDialogModal(id)}
              >
                <Tooltip title={t("Start Working On Complaint")}>
                  <img src={InprogressIcon} height={20} />
                </Tooltip>
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setComplaintIdForCloseStatusDialogModal(id)}
                disabled={status === "close"}
              >
                <Tooltip title={t("close complaint")}>
                  <DoneIcon
                    fontSize="small"
                    color={status === "close" ? "disabled" : "success"}
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}

            {!isReadAdmin && (
              <IconButton
                onClick={() => {
                  setNewUtilityData({
                    utilityId: utility?.id,
                  });
                  setIsOpenNewUtilityModal(true);
                }}
                disabled={
                  !!utilityIssueName ||
                  complaintStatus === getOptionValue("complaintStatus", "close")
                }
              >
                <Tooltip title={t("Add New Issue")}>
                  <AddBoxIcon
                    fontSize="small"
                    color={
                      utilityIssueName ||
                      complaintStatus ===
                        getOptionValue("complaintStatus", "close")
                        ? "disabled"
                        : "primary"
                    }
                  />
                </Tooltip>
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      title: "Status",
      renderCell: ({ complaintStatus }) => (
        <Chip
          label={getOptionLabel("complaintStatus", complaintStatus)}
          color={chipColor[complaintStatus]}
          size="small"
        />
      ),

      columnProps: {
        width: "5%",
      },
    },
  ];

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const handleEditComplaintStatus = async (complaintStatus, complaintId) => {
    try {
      await updateComplaintStatus(null, complaintId, {
        complaintStatus,
      });
      setComplaintIdForOpenStatusDialogModal(null);
      setComplaintIdForCloseStatusDialogModal(null);
    } catch (err) {}
  };

  const renderSubRow = (data) => {
    return data.description ? (
      <ComplaintDescription>
        <p className="title">{t("Complaint Description")}:</p>
        <p className="description">{data.description}</p>
      </ComplaintDescription>
    ) : (
      <p className="text-center mb-1">{t("No Description")}</p>
    );
  };

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/consumer-complaint",
      tags: [CONSUMER_COMPLAINT],
      query,
    }),
    [query]
  );

  useEffect(() => {
    if (complaintIdForViewDocument) {
      (async () => {
        try {
          const { data: { complaintDocuments = [] } = {} } =
            await getDocumentUrlsById(complaintIdForViewDocument);
          setDocumentUrls(complaintDocuments);
        } catch (error) {}
      })();
    } else if (documentUrls.length) {
      setDocumentUrls([]);
    }
  }, [complaintIdForViewDocument]);

  return (
    <>
      <Backdrop open={isLoading || isFetching} />
      <FilterComplaintList setQuery={setQuery} resetPage={handleResetPage} />
      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        isExpandable
        renderSubRow={renderSubRow}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 400px)"
            : md
            ? "calc(100vh - 350px)"
            : lg
            ? "calc(100vh - 300px)"
            : "calc(100vh - 260px)"
        }
      />
      {!!complaintId && (
        <ComplaintConversation
          complaintStatus={complaintStatus}
          isOpen={!!complaintId}
          handleClose={() => {
            setSearchParams({});
          }}
          complaintId={Number(complaintId)}
        />
      )}
      {!!(
        complaintIdForViewDocument &&
        !isLoading &&
        !isFetching &&
        documentUrls?.length
      ) && (
        <Modal
          isOpen
          handleClose={() => {
            searchParams.delete("complaintIdForDocumentView");
            setSearchParams(searchParams);
          }}
          title="Documents"
          maxWidth="md"
        >
          <ImageGalleryModal images={documentUrls} />
        </Modal>
      )}
      {!!complaintIdForOpenStatusDialogModal && (
        <ConfirmationDialog
          message={t(
            "Are you sure you want to start working on this complaint?"
          )}
          onCancel={() => setComplaintIdForOpenStatusDialogModal(null)}
          onConfirm={() =>
            handleEditComplaintStatus(
              "open",
              complaintIdForOpenStatusDialogModal
            )
          }
          isOpen={!!complaintIdForOpenStatusDialogModal}
          title="Warning"
          variant="warning"
        />
      )}
      {!!complaintIdForCloseStatusDialogModal && (
        <ConfirmationDialog
          message="Are you sure you want to close this complaint?"
          onCancel={() => setComplaintIdForCloseStatusDialogModal(null)}
          onConfirm={() =>
            handleEditComplaintStatus(
              "close",
              complaintIdForCloseStatusDialogModal
            )
          }
          isOpen={!!complaintIdForCloseStatusDialogModal}
          title="Warning"
          variant="warning"
        />
      )}
      {isOpenNewUtilityModal && (
        <AddNewUtilityIssue
          handleClose={() => {
            setIsOpenNewUtilityModal(false);
          }}
          isOpen={isOpenNewUtilityModal}
          newUtilityData={newUtilityData}
        />
      )}
    </>
  );
};

ViewAllComplaints.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ViewAllComplaints);
